import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { LinearProgress } from '@mui/material';
import TextField from '../../common/TextField';
import { Business } from '../../../types/business.type';
import { getSearchsBusiness } from '../../../services/business.service';
import LeftArrowIcon from '../../common/Icons/LeftArrowIcon';
import ClearTextField from '../../common/Icons/ClearTextField';
import ServiceDirectoryBusinessList from '../ServiceDirectoryBusinessList';
import { BusinessDisplayUnit } from '../../../types/business-group-type';
import { GetSearchBusinessWithBusinessGroup } from '../../../services/business-group.service';
import SearchBusinessWithBusinessGroupList from './SearchBusinessWithBusinessGroupList';

interface ServiceDirectoryBusinessListProps {
    closeSearchModal: () => void;
    onSelect: (businessDisplayUnit: BusinessDisplayUnit) => void;
}

const StyledSearchBusinessDirectory = styled.div`
    .search-menu-content {
        width: 100%;
        height: 70px;
        margin-top: 15px;
        padding-left: 20px;
        padding-right: 20px;
        justify-content: space-between;
        display: flex;
    }

    .back-icon-content {
        margin-top: 10px;
    }

    .back-icon {
        width: 30px;
        height: 30px;
        cursor: pointer;
        margin-right: 25px;
    }

    .search-text-field {
        width: 100%;
    }

    .clear-text-icon {
        width: 20px;
        height: 20px;
        position: absolute;
        right: 28px;
        top: 30px;
        cursor: pointer;
    }

    .business-list {
        overflow: scroll;
        margin-top: 10px;
        height: calc(100vh - 13rem);
        max-height: 1500px;
    }
    .divider {
        border: 0.7px solid #e8e8e8;
    }
    .no-business {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-top: 10px;
    }
`;

const SearchBusinessDirectory = ({
    closeSearchModal,
    onSelect,
}: ServiceDirectoryBusinessListProps) => {
    const [showClearTextIcon, setShowClearTextIcon] = useState<boolean>(false);
    const searchTextRef = useRef<HTMLInputElement | null>(null);
    const [searchTextValue, setSearchTextValue] = useState<string>('');
    const [searchBusinessTimeRecord, setSearchBusinessTimeRecord] = useState<number>();
    const [businessList, setBusinessList] = useState<Business[]>([]);
    const [businessDisplayUnitList, setBusinessDisplayUnitList] = useState<BusinessDisplayUnit[]>(
        [],
    );
    const [loading, setLoading] = useState<boolean>(false);
    const [isNoBusiness, setIsNoBusiness] = useState<boolean>(false);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [limit, setLimit] = useState<number>(30);
    const [offset, setOffset] = useState<number>(0);
    const [onFetchMoreBusiness, setOnFetchMoreBusiness] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const startSearching = () => {
        if (searchTextRef.current) {
            const { value } = searchTextRef.current;
            setBusinessDisplayUnitList([]);
            setOnFetchMoreBusiness(false);
            setOffset(0);
            if (value) {
                setShowClearTextIcon(true);
                setSearchTextValue(value);
                setIsNoBusiness(false);
            } else {
                clearTimeout(searchBusinessTimeRecord as number);
                setShowClearTextIcon(false);
                setLoading(false);
                setHasMore(false);
                setSearchTextValue('');
                setIsNoBusiness(false);
            }
        }
    };

    const scrollSearchBusinessList = async (event: any) => {
        const { target } = event;
        if (hasMore && target.scrollHeight - target.scrollTop - target.clientHeight < 1) {
            if (limit * (offset + 1) < totalCount) {
                setOnFetchMoreBusiness(true);
                setHasMore(true);
                setOffset(offset + 1);
            } else {
                setHasMore(false);
                setOnFetchMoreBusiness(false);
            }
        }
    };

    useEffect(() => {
        if (searchTextValue) {
            clearTimeout(searchBusinessTimeRecord);
            if (!onFetchMoreBusiness) {
                setLoading(true);
            }

            const timeout = window.setTimeout(async () => {
                const payload = {
                    searchData: searchTextValue,
                    limit,
                    offset,
                };
                // const result = await getSearchsBusiness(payload);
                const result = await GetSearchBusinessWithBusinessGroup(payload);
                setLoading(false);
                if (result.data?.businessDisplayUnitList?.length === 0) {
                    setIsNoBusiness(true);
                    setBusinessDisplayUnitList([]);
                } else {
                    // const List = businessList.concat(result.data?.business as Business[]);
                    // setBusinessList(List);
                    const List = businessDisplayUnitList.concat(
                        result.data?.businessDisplayUnitList as BusinessDisplayUnit[],
                    );
                    setBusinessDisplayUnitList(List);
                    setTotalCount(result.data?.totalCount as number);
                    if (limit * (offset + 1) > (result.data?.totalCount as number)) {
                        setHasMore(false);
                        setOnFetchMoreBusiness(false);
                    } else {
                        setHasMore(true);
                        setOnFetchMoreBusiness(true);
                    }
                }
            }, 1000);
            setSearchBusinessTimeRecord(timeout as number);
        }
    }, [searchTextValue, offset]);

    const clearSearchText = () => {
        if (searchTextRef.current) {
            searchTextRef.current.value = '';
            setSearchTextValue('');
            setBusinessDisplayUnitList([]);
            setShowClearTextIcon(false);
            setIsNoBusiness(false);
            setHasMore(false);
            setOffset(0);
            setOnFetchMoreBusiness(false);
        }
    };

    return (
        <StyledSearchBusinessDirectory>
            <div className="search-menu-content">
                <div className="back-icon-content">
                    <LeftArrowIcon className="back-icon" onClick={closeSearchModal} />
                </div>

                <TextField
                    inputRef={searchTextRef}
                    placeholder="Search"
                    className="search-text-field"
                    onChange={startSearching}
                />

                <div className="linear">
                    {showClearTextIcon && (
                        <ClearTextField className="clear-text-icon" onClick={clearSearchText} />
                    )}
                </div>
            </div>

            {loading && <LinearProgress />}
            <div className="divider" />

            {!isNoBusiness ? (
                <div
                    className="business-list"
                    onScroll={(e) => {
                        scrollSearchBusinessList(e);
                    }}
                >
                    <SearchBusinessWithBusinessGroupList
                        onSelect={onSelect}
                        businessDisplayUnitList={businessDisplayUnitList}
                    />
                    {hasMore && <LinearProgress />}
                </div>
            ) : (
                <div className="no-business">No business found</div>
            )}
        </StyledSearchBusinessDirectory>
    );
};

export default SearchBusinessDirectory;
